import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Clock,
  Download,
  Eye,
  FileCheck,
  MapPin,
  Paperclip,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { AppModal } from '../../../components/ui/app-modal';
import { Button } from '../../../components/ui/button';
import { DatetimePicker } from '../../../components/ui/datetimePicker';
import { FieldBox } from '../../../components/ui/field-box';
import { FileDropZone } from '../../../components/ui/file-drop-zone';
import { usePermissions } from '../../../hooks/use-permissions';
import { api } from '../../../lib/ky';
import { cn } from '../../../lib/utils';
import {
  AttachmentPreviewModal,
  CompleteModal,
  type PreviewAttachment,
  RescheduleModal,
  type SiteVisitStatus,
  StatusBadge,
  fmt as fmtVisit,
} from './shared-visit-ui';

// ── Types ──────────────────────────────────────────────────────────────────────

type StudyReportStatus = 'DRAFT' | 'WAITING_FOR_APPROVAL' | 'APPROVED' | 'REJECTED';

interface StudyReportAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  uploadedAt: string;
}

interface ApprovalRecord {
  id: string;
  decision: StudyReportStatus;
  comments: string | null;
  reviewedAt: string;
  reviewedBy: { id: string; name: string; email: string };
}

interface StudyReport {
  id: string;
  studyName: string | null;
  status: StudyReportStatus;
  cycleNumber: number;
  comments: string | null;
  submittedAt: string | null;
  dateOfApproval: string | null;
  createdAt: string;
  createdBy: { id: string; name: string };
  attachments: StudyReportAttachment[];
  approvals: ApprovalRecord[];
}

interface SmSiteVisit {
  id: string;
  status: SiteVisitStatus;
  name: string | null;
  address: string | null;
  scheduledAt: string | null;
  completedAt: string | null;
  notes: string | null;
}

interface SiteVisitsResponse {
  SM_CONSULTANT: SmSiteVisit[];
}

// ── Config ─────────────────────────────────────────────────────────────────────

const STATUS_CFG: Record<StudyReportStatus, { label: string; className: string }> = {
  DRAFT: {
    label: 'Draft',
    className: 'bg-gray-100 text-gray-600 border-gray-200',
  },
  WAITING_FOR_APPROVAL: {
    label: 'Awaiting Approval',
    className: 'bg-warning-50 text-warning-600 border-warning-200',
  },
  APPROVED: {
    label: 'Approved',
    className: 'bg-success-50 text-success-600 border-success-200',
  },
  REJECTED: {
    label: 'Rejected',
    className: 'bg-error-50 text-error-600 border-error-200',
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmt(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

function fmtBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Report Form (create / edit draft) ──────────────────────────────────────────

function ReportForm({
  report,
  studyRequestId,
  onMutated,
  nameInputRef,
}: {
  report: StudyReport | null;
  studyRequestId: string;
  onMutated: () => void;
  nameInputRef?: React.RefObject<HTMLInputElement | null>;
}) {
  const qc = useQueryClient();
  const [studyName, setStudyName] = useState(report?.studyName ?? '');
  const [comments, setComments] = useState(report?.comments ?? '');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [previewAttachment, setPreviewAttachment] = useState<PreviewAttachment | null>(null);

  const reportKey = ['study-reports', studyRequestId];
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: reportKey });
    onMutated();
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      let currentReportId = report?.id;
      if (!currentReportId) {
        const created = await api
          .post(`study-requests/${studyRequestId}/study-reports`, {
            json: { studyName: studyName || undefined, comments: comments || undefined },
          })
          .json<{ id: string }>();
        currentReportId = created.id;
      } else {
        await api
          .patch(`study-requests/${studyRequestId}/study-reports/${currentReportId}`, {
            json: { studyName: studyName || undefined, comments: comments || undefined },
          })
          .json();
      }
      if (pendingFile) {
        const fd = new FormData();
        fd.append('file', pendingFile);
        await api
          .post(`study-requests/${studyRequestId}/study-reports/${currentReportId}/attachments`, {
            body: fd,
          })
          .json();
      }
    },
    onSuccess: () => {
      setPendingFile(null);
      invalidate();
    },
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: (attachmentId: string) =>
      api.delete(
        `study-requests/${studyRequestId}/study-reports/${report?.id}/attachments/${attachmentId}`,
      ),
    onSuccess: invalidate,
  });

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      {report?.status === 'REJECTED' && report.approvals.length > 0 && (
        <div className="border-b border-error-100 bg-error-50 px-5 py-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-error-500">
            Rejection Reason
          </p>
          <p className="text-sm text-error-700 leading-relaxed">
            {report.approvals[0].comments ?? '—'}
          </p>
          <p className="mt-1.5 text-xs text-error-400">
            by {report.approvals[0].reviewedBy.name} · {fmt(report.approvals[0].reviewedAt)}
          </p>
        </div>
      )}

      <div className="space-y-4 p-5">
        <div>
          <label
            className="mb-1.5 block text-xs font-medium text-gray-700"
            htmlFor="study-report-name"
          >
            Report Name
            <span className="ml-1 text-gray-400">(optional)</span>
          </label>
          <input
            id="study-report-name"
            ref={nameInputRef}
            type="text"
            value={studyName}
            onChange={(e) => setStudyName(e.target.value)}
            placeholder="e.g. Environmental Impact Assessment Report"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div>
          <label
            className="mb-1.5 block text-xs font-medium text-gray-700"
            htmlFor="study-report-comments"
          >
            Comments / Notes
            <span className="ml-1 text-gray-400">(optional)</span>
          </label>
          <textarea
            id="study-report-comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            placeholder="Remarks or summary…"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>

        <div>
          <p className="mb-1 text-xs font-medium text-gray-700">Study Report Document</p>
          <p className="mb-2 text-xs text-gray-400">Required before submitting for SM approval.</p>

          {report && report.attachments.length > 0 && (
            <div className="mb-2 space-y-1.5">
              {report.attachments.map((att) => (
                <AttachmentRow
                  key={att.id}
                  att={att}
                  reportId={report.id}
                  studyRequestId={studyRequestId}
                  onPreview={setPreviewAttachment}
                  onDelete={() => deleteAttachmentMutation.mutate(att.id)}
                />
              ))}
            </div>
          )}

          <FileDropZone
            file={pendingFile}
            onChange={setPendingFile}
            onError={setFileError}
            hint="PDF, DOCX, JPG, PNG — max 10 MB"
          />
          {fileError && <p className="mt-1 text-xs text-error-500">{fileError}</p>}
        </div>

        {saveMutation.isError && (
          <p className="text-xs text-error-500">Failed to save. Try again.</p>
        )}
        {deleteAttachmentMutation.isError && (
          <p className="text-xs text-error-500">Failed to delete attachment. Try again.</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            {saveMutation.isPending ? 'Saving…' : 'Save Draft'}
          </Button>
        </div>
      </div>

      <AttachmentPreviewModal
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />
    </div>
  );
}

// ── Reject Modal ───────────────────────────────────────────────────────────────

function RejectModal({
  open,
  onClose,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (comments: string) => void;
  isPending: boolean;
}) {
  const [comments, setComments] = useState('');

  function handleClose() {
    setComments('');
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!comments.trim()) return;
    onSubmit(comments.trim());
  }

  return (
    <AppModal open={open} onClose={handleClose} title="Reject Study Report">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600">
          Provide mandatory rejection comments. These will be sent to the Beeah Consultant.
        </p>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-700">
            Rejection Comments <span className="text-error-500">*</span>
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={4}
            required
            placeholder="Describe the reason for rejection and what needs to be corrected…"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          <p className="mt-1 text-xs text-gray-400">
            {comments.trim().length === 0
              ? 'Comments are required'
              : `${comments.trim().length} characters`}
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={isPending || !comments.trim()}
            className="bg-error-500 hover:bg-error-600 focus:ring-error-200"
          >
            {isPending ? 'Rejecting…' : 'Reject Report'}
          </Button>
        </div>
      </form>
    </AppModal>
  );
}

// ── Approve Confirm Modal ──────────────────────────────────────────────────────

function ApproveModal({
  open,
  onClose,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (comments: string) => void;
  isPending: boolean;
}) {
  const [comments, setComments] = useState('');

  function handleClose() {
    setComments('');
    onClose();
  }

  return (
    <AppModal open={open} onClose={handleClose} title="Approve Study Report">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Approving this report will advance the study request to the{' '}
          <strong>Payment Process</strong> stage and send a payment link to the customer.
        </p>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-700">
            Approval Comments
            <span className="ml-1 text-gray-400">(optional)</span>
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            placeholder="Optional comments for the record…"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={isPending}
            onClick={() => onConfirm(comments)}
            className="bg-success-500 hover:bg-success-600 focus:ring-success-200"
          >
            <CheckCircle2 size={13} className="mr-1" />
            {isPending ? 'Approving…' : 'Confirm Approval'}
          </Button>
        </div>
      </div>
    </AppModal>
  );
}

// ── Schedule SM Consultant Site Visit Modal ────────────────────────────────────

function ScheduleSmVisitModal({
  open,
  defaultAddress,
  onClose,
  onSubmit,
  isPending,
}: {
  open: boolean;
  defaultAddress?: string;
  onClose: () => void;
  onSubmit: (form: { name: string; address: string; visitDate: string; comments: string }) => void;
  isPending: boolean;
}) {
  const [form, setForm] = useState({ name: '', address: '', visitDate: '', comments: '' });

  useEffect(() => {
    if (open) setForm({ name: '', address: defaultAddress ?? '', visitDate: '', comments: '' });
  }, [open, defaultAddress]);

  const isValid = !!form.visitDate && form.name.trim().length > 0;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Schedule SM Consultant Site Visit"
      description="Visit name and scheduled date & time are required — other fields can be filled in later."
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600" htmlFor="sm-visit-name">
            Visit Name <span className="text-error-500">*</span>
          </label>
          <input
            id="sm-visit-name"
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Final Inspection"
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600" htmlFor="sm-visit-address">
            Location / Address
          </label>
          <input
            id="sm-visit-address"
            type="text"
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600">
            Scheduled Date &amp; Time <span className="text-error-500">*</span>
          </label>
          <DatetimePicker
            value={form.visitDate ? new Date(form.visitDate) : undefined}
            onChange={(date) =>
              setForm((f) => ({ ...f, visitDate: date ? date.toISOString() : '' }))
            }
            granularity="minute"
            placeholder="Pick date & time"
            className="w-full"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600" htmlFor="sm-visit-comments">
            Comments
          </label>
          <textarea
            id="sm-visit-comments"
            rows={3}
            value={form.comments}
            onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
            placeholder="Any notes or instructions…"
            className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={() => onSubmit(form)}
            disabled={!isValid || isPending}
          >
            {isPending ? 'Scheduling…' : 'Schedule Visit'}
          </Button>
        </div>
      </div>
    </AppModal>
  );
}

// ── SM Consultant Site Visit Section ───────────────────────────────────────────

function SmVisitSection({
  requestId,
  visit,
  canAct,
  scheduleTrigger,
  defaultAddress,
  onMutated,
}: {
  requestId: string;
  visit: SmSiteVisit | undefined;
  canAct: boolean;
  scheduleTrigger?: number;
  defaultAddress?: string;
  onMutated: () => void;
}) {
  const qc = useQueryClient();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  useEffect(() => {
    if (scheduleTrigger) setScheduleOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleTrigger]);

  const visitsKey = ['site-visits', requestId];
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: visitsKey });
    onMutated();
  };

  const createMutation = useMutation({
    mutationFn: (form: { name: string; address: string; visitDate: string; comments: string }) =>
      api
        .post(`study-requests/${requestId}/site-visits`, {
          json: {
            visitType: 'SM_CONSULTANT',
            name: form.name || undefined,
            address: form.address || undefined,
            visitDate: form.visitDate || undefined,
            comments: form.comments || undefined,
          },
        })
        .json(),
    onSuccess: () => {
      setScheduleOpen(false);
      invalidate();
    },
  });

  const startMutation = useMutation({
    mutationFn: () =>
      api.patch(`study-requests/${requestId}/site-visits/${visit?.id}/start`).json(),
    onSuccess: invalidate,
  });

  const completeMutation = useMutation({
    mutationFn: (comments: string) =>
      api
        .patch(`study-requests/${requestId}/site-visits/${visit?.id}/complete`, {
          json: comments ? { comments } : {},
        })
        .json(),
    onSuccess: () => {
      setCompleteOpen(false);
      invalidate();
    },
  });

  const rescheduleMutation = useMutation({
    mutationFn: (form: { scheduledAt: string; reason: string }) =>
      api
        .patch(`study-requests/${requestId}/site-visits/${visit?.id}/reschedule`, { json: form })
        .json(),
    onSuccess: () => {
      setRescheduleOpen(false);
      invalidate();
    },
  });

  if (!visit) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50/50 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-700">SM Consultant Site Visit</h3>
        </div>
        <div className="px-5 py-10 text-center">
          <Calendar size={32} className="mx-auto mb-3 text-gray-200" />
          <p className="text-sm font-medium text-gray-500">No site visit scheduled yet</p>
          {canAct ? (
            <Button variant="outline" className="mt-4" onClick={() => setScheduleOpen(true)}>
              Schedule SM Consultant Site Visit
            </Button>
          ) : (
            <p className="mt-1 text-xs text-gray-400">
              An SM Consultant can schedule a site visit at any point.
            </p>
          )}
        </div>
        <ScheduleSmVisitModal
          open={scheduleOpen}
          defaultAddress={defaultAddress}
          onClose={() => setScheduleOpen(false)}
          onSubmit={(form) => createMutation.mutate(form)}
          isPending={createMutation.isPending}
        />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/50 px-5 py-3">
        <h3 className="text-sm font-semibold text-gray-700">SM Consultant Site Visit</h3>
        <StatusBadge status={visit.status} />
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 px-5 py-5">
        <FieldBox icon={<ClipboardList size={16} />} label="Visit Name">
          {visit.name ?? '—'}
        </FieldBox>
        <FieldBox icon={<MapPin size={16} />} label="Location / Address">
          {visit.address ?? '—'}
        </FieldBox>
        <FieldBox icon={<CalendarClock size={16} />} label="Scheduled Date & Time">
          {fmtVisit(visit.scheduledAt) ?? '—'}
        </FieldBox>
        {visit.completedAt && (
          <FieldBox icon={<CheckCircle2 size={16} />} label="Completed At">
            {fmtVisit(visit.completedAt)}
          </FieldBox>
        )}
        {visit.notes && (
          <FieldBox icon={<ClipboardList size={16} />} label="Notes">
            {visit.notes}
          </FieldBox>
        )}
      </div>
      {canAct && visit.status !== 'COMPLETED' && (
        <div className="flex flex-wrap gap-2 border-t border-gray-100 px-5 py-4">
          {(visit.status === 'NOT_STARTED' || visit.status === 'APPROVED') && (
            <Button
              variant="outline"
              disabled={startMutation.isPending}
              onClick={() => startMutation.mutate()}
            >
              Mark In Progress
            </Button>
          )}
          {visit.status === 'IN_PROGRESS' && (
            <Button
              variant="primary"
              className="bg-success-500 hover:bg-success-600"
              onClick={() => setCompleteOpen(true)}
            >
              Mark Completed
            </Button>
          )}
          <Button variant="outline" onClick={() => setRescheduleOpen(true)}>
            Reschedule
          </Button>
        </div>
      )}
      <CompleteModal
        open={completeOpen}
        onClose={() => setCompleteOpen(false)}
        onSubmit={(comments) => completeMutation.mutate(comments)}
        isPending={completeMutation.isPending}
      />
      <RescheduleModal
        open={rescheduleOpen}
        currentScheduledAt={visit.scheduledAt}
        onClose={() => setRescheduleOpen(false)}
        onSubmit={(form) => rescheduleMutation.mutate(form)}
        isPending={rescheduleMutation.isPending}
      />
    </div>
  );
}

// ── Attachment Row ─────────────────────────────────────────────────────────────

function AttachmentRow({
  att,
  reportId,
  studyRequestId,
  onPreview,
  onDelete,
}: {
  att: StudyReportAttachment;
  reportId: string;
  studyRequestId: string;
  onPreview: (attachment: PreviewAttachment) => void;
  onDelete?: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  async function fetchDownloadUrl() {
    return api
      .get(
        `study-requests/${studyRequestId}/study-reports/${reportId}/attachments/${att.id}/download`,
      )
      .json<{ url: string; fileName: string }>();
  }

  async function handleDownload() {
    setDownloading(true);
    try {
      const res = await fetchDownloadUrl();
      const a = document.createElement('a');
      a.href = res.url;
      a.download = res.fileName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    } finally {
      setDownloading(false);
    }
  }

  async function handlePreview() {
    setPreviewing(true);
    try {
      const res = await fetchDownloadUrl();
      onPreview({ fileName: res.fileName, mimeType: att.mimeType, url: res.url });
    } finally {
      setPreviewing(false);
    }
  }

  return (
    <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-1.5">
      <Paperclip size={12} className="shrink-0 text-gray-400" />
      <span className="flex-1 truncate text-xs font-medium text-gray-700">{att.fileName}</span>
      <span className="shrink-0 text-xs text-gray-400">{fmtBytes(att.fileSizeBytes)}</span>
      <button
        type="button"
        onClick={handlePreview}
        disabled={previewing}
        className="flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-medium text-brand-600 hover:bg-brand-50 disabled:opacity-50 transition-colors"
      >
        <Eye size={11} />
        {previewing ? 'Loading…' : 'Preview'}
      </button>
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        className="flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-medium text-brand-600 hover:bg-brand-50 disabled:opacity-50 transition-colors"
      >
        <Download size={11} />
        {downloading ? 'Loading…' : 'Download'}
      </button>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="shrink-0 text-gray-400 hover:text-error-500"
          aria-label="Delete attachment"
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

// ── Report Card ────────────────────────────────────────────────────────────────

function ReportCard({
  report,
  studyRequestId,
  canApprove,
  onMutated,
  approveTrigger,
  rejectTrigger,
}: {
  report: StudyReport;
  studyRequestId: string;
  canApprove: boolean;
  onMutated: () => void;
  approveTrigger?: number;
  rejectTrigger?: number;
}) {
  const qc = useQueryClient();
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<PreviewAttachment | null>(null);

  useEffect(() => {
    if (approveTrigger) setApproveOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approveTrigger]);

  useEffect(() => {
    if (rejectTrigger) setRejectOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rejectTrigger]);

  const base = `study-requests/${studyRequestId}/study-reports/${report.id}`;
  const reportKey = ['study-reports', studyRequestId];

  const isAwaitingApproval = report.status === 'WAITING_FOR_APPROVAL';

  const approveMutation = useMutation({
    mutationFn: (comments: string) =>
      api.post(`${base}/approve`, { json: { comments: comments || undefined } }).json(),
    onSuccess: () => {
      setApproveOpen(false);
      qc.invalidateQueries({ queryKey: reportKey });
      onMutated();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (comments: string) => api.post(`${base}/reject`, { json: { comments } }).json(),
    onSuccess: () => {
      setRejectOpen(false);
      qc.invalidateQueries({ queryKey: reportKey });
      onMutated();
    },
  });

  const cfg = STATUS_CFG[report.status];

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      {/* Header row */}
      <div className="flex w-full items-center gap-3 px-5 py-4 text-left">
        <FileCheck size={16} className="shrink-0 text-brand-500" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-gray-800">
              {report.studyName || `Study Report #${report.cycleNumber}`}
            </p>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-500">
              Cycle {report.cycleNumber}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-gray-400">
            Submitted by {report.createdBy.name} · {fmt(report.submittedAt || report.createdAt)}
            {report.dateOfApproval && ` · Approved ${fmt(report.dateOfApproval)}`}
          </p>
        </div>

        <span
          className={cn(
            'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold shrink-0',
            cfg.className,
          )}
        >
          {cfg.label}
        </span>
      </div>

      {/* Content */}
      <div className="border-t border-gray-100 px-5 py-4 space-y-5">
        {/* Comments */}
        {report.comments && (
          <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
              Notes
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">{report.comments}</p>
          </div>
        )}

        {/* Reject feedback from last approval cycle */}
        {report.status === 'REJECTED' && report.approvals.length > 0 && (
          <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-error-500">
              Rejection Reason
            </p>
            <p className="text-sm text-error-700 leading-relaxed">
              {report.approvals[0].comments ?? '—'}
            </p>
            <p className="mt-1.5 text-xs text-error-400">
              by {report.approvals[0].reviewedBy.name} · {fmt(report.approvals[0].reviewedAt)}
            </p>
          </div>
        )}

        {/* Attachments */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Attachments
            {report.attachments.length > 0 && (
              <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px]">
                {report.attachments.length}
              </span>
            )}
          </p>

          {report.attachments.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 p-4 text-center">
              <p className="text-xs text-gray-400">No attachments</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {report.attachments.map((att) => (
                <AttachmentRow
                  key={att.id}
                  att={att}
                  reportId={report.id}
                  studyRequestId={studyRequestId}
                  onPreview={setPreviewAttachment}
                />
              ))}
            </div>
          )}
        </div>

        {/* Approve / Reject actions — SM Consultant only, DRAFT/WAITING_FOR_APPROVAL only */}
        {canApprove && isAwaitingApproval && (
          <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
            <p className="flex-1 text-xs text-gray-500">
              Review complete? Approve to advance to payment, or reject with comments. Scheduling an
              SM Consultant site visit is optional and not required to approve or reject.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRejectOpen(true)}
              className="border-error-200 text-error-600 hover:bg-error-50"
            >
              <X size={13} className="mr-1" />
              Reject
            </Button>
            <Button
              size="sm"
              onClick={() => setApproveOpen(true)}
              className="bg-success-500 hover:bg-success-600 focus:ring-success-200"
            >
              <CheckCircle2 size={13} className="mr-1" />
              Approve
            </Button>
          </div>
        )}

        {/* Approval History */}
        {report.approvals.length > 0 && (
          <div>
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Approval History ({report.approvals.length})
            </p>

            <div className="mt-2 space-y-2">
              {report.approvals.map((appr) => {
                const isApproved = appr.decision === 'APPROVED';
                return (
                  <div
                    key={appr.id}
                    className={cn(
                      'rounded-xl border px-4 py-3',
                      isApproved
                        ? 'border-success-200 bg-success-50'
                        : 'border-error-200 bg-error-50',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={cn(
                          'text-xs font-semibold',
                          isApproved ? 'text-success-600' : 'text-error-600',
                        )}
                      >
                        {isApproved ? '✓ Approved' : '✗ Rejected'}
                      </span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock size={10} />
                        {fmt(appr.reviewedAt)}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500">by {appr.reviewedBy.name}</p>
                    {appr.comments && (
                      <p className="mt-2 text-xs text-gray-700 leading-relaxed">{appr.comments}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <ApproveModal
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        onConfirm={(comments) => approveMutation.mutate(comments)}
        isPending={approveMutation.isPending}
      />
      <RejectModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onSubmit={(comments) => rejectMutation.mutate(comments)}
        isPending={rejectMutation.isPending}
      />
      <AttachmentPreviewModal
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function StudyReportTab({
  requestId,
  defaultAddress,
  onMutated,
  autoOpenTrigger,
  approveTrigger,
  rejectTrigger,
  scheduleSmVisitTrigger,
}: {
  requestId: string;
  defaultAddress?: string;
  onMutated: () => void;
  autoOpenTrigger?: number;
  approveTrigger?: number;
  rejectTrigger?: number;
  scheduleSmVisitTrigger?: number;
}) {
  const { can } = usePermissions();
  const canCreate = can('sm-study-report:create');
  const canView = can('sm-study-report:view');
  // Approve/Reject/Schedule-SM-Visit is SM Consultant only: has the SM permission but NOT lab:create
  const canApprove = can('sm-study-report:approve') && !can('sm-lab:create');
  const canActOnSmVisit = can('sm-site-visit:create') && !can('sm-lab:create');
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoOpenTrigger) nameInputRef.current?.focus();
  }, [autoOpenTrigger]);

  const reportKey = ['study-reports', requestId];

  const {
    data: reports,
    isLoading,
    isError,
  } = useQuery({
    queryKey: reportKey,
    queryFn: () => api.get(`study-requests/${requestId}/study-reports`).json<StudyReport[]>(),
    enabled: canView,
  });

  const { data: visitsData } = useQuery({
    queryKey: ['site-visits', requestId],
    queryFn: () => api.get(`study-requests/${requestId}/site-visits`).json<SiteVisitsResponse>(),
    enabled: canView,
  });

  const smVisit = visitsData?.SM_CONSULTANT[0];

  const latest = reports && reports.length > 0 ? reports[reports.length - 1] : null;
  const olderReports = reports && reports.length > 1 ? reports.slice(0, -1) : [];
  const isLatestEditable = !latest || latest.status === 'DRAFT' || latest.status === 'REJECTED';

  if (!canView) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        <FileCheck size={32} className="mx-auto mb-3 text-gray-200" />
        <p className="text-sm text-gray-400">You don't have permission to view study reports.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800">Study Report</h3>
        <p className="text-xs text-gray-400">
          Prepare the study report, then use the "Submit for SM Approval" button at the top of the
          page.
        </p>
      </div>

      {/* SM Consultant Site Visit — can be scheduled at any point in the workflow */}
      <SmVisitSection
        requestId={requestId}
        visit={smVisit}
        canAct={canActOnSmVisit}
        scheduleTrigger={scheduleSmVisitTrigger}
        defaultAddress={defaultAddress}
        onMutated={onMutated}
      />

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-2xl bg-gray-100" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="rounded-2xl border border-error-200 bg-error-50 p-6 text-center">
          <p className="text-sm font-medium text-error-600">Failed to load study reports</p>
          <p className="mt-1 text-xs text-error-500">Please refresh the page</p>
        </div>
      )}

      {!isLoading && !isError && (
        <>
          {olderReports.map((report) => (
            <ReportCard
              key={report.id}
              report={report}
              studyRequestId={requestId}
              canApprove={canApprove}
              onMutated={onMutated}
            />
          ))}

          {isLatestEditable ? (
            canCreate ? (
              <ReportForm
                report={latest}
                studyRequestId={requestId}
                onMutated={onMutated}
                nameInputRef={nameInputRef}
              />
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center shadow-sm">
                <FileCheck size={36} className="mx-auto mb-3 text-gray-200" />
                <p className="text-sm font-semibold text-gray-500">
                  {latest ? 'Study report draft is being prepared' : 'No study report yet'}
                </p>
              </div>
            )
          ) : (
            latest && (
              <ReportCard
                report={latest}
                studyRequestId={requestId}
                canApprove={canApprove}
                onMutated={onMutated}
                approveTrigger={approveTrigger}
                rejectTrigger={rejectTrigger}
              />
            )
          )}
        </>
      )}
    </div>
  );
}
